import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Button, ABSpinner } from '@abroad/components'
import API from '../../utils/API'

const PREVIEW_DEBOUNCE_MS = 500

// The preview is read-only: links and buttons show a disabled cursor, and any link
// opens in a new window, which the empty iframe sandbox blocks.
const PREVIEW_ONLY_STYLE = `<base target="_blank"><style>a, button, input, [role="button"] { cursor: not-allowed !important; }</style>`

const withPreviewOnlyStyle = (html = '') => {
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(
      /<head[^>]*>/i,
      (match) => `${match}${PREVIEW_ONLY_STYLE}`,
    )
  }
  return `${PREVIEW_ONLY_STYLE}${html}`
}

/**
 * Shows the scheduling email a client receives when a platform-scheduled SOW goes live
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is open
 * @param {Function} props.onHide - Close handler
 * @param {Object} props.payload - Body for POST /sows/email-preview
 * @param {boolean} props.isPaymentRequired - Whether the SOW waits for payment first
 * @param {boolean} props.isCoachMissingVideoCallLink - Whether the coach has no video call link
 */
const SOWEmailPreviewModal = ({
  show,
  onHide,
  payload,
  isPaymentRequired,
  isCoachMissingVideoCallLink,
}) => {
  const [preview, setPreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const requestIdRef = useRef(0)

  // Compare by value so a parent re-render with the same values does not refetch
  const payloadKey = payload ? JSON.stringify(payload) : ''

  useEffect(() => {
    if (!show || !payloadKey) {
      // Closed: drop any response still in flight so it cannot reach the next open
      requestIdRef.current += 1
      return undefined
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    // Each open starts empty and shows the spinner, never the previous email
    setPreview(null)
    setErrorMessage('')
    setIsLoading(true)

    const timer = setTimeout(async () => {
      try {
        const { data } = await API.AdminAPI.coachPlan.getSowEmailPreview(
          JSON.parse(payloadKey),
        )
        if (requestId === requestIdRef.current) {
          setPreview(data)
        }
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setErrorMessage(
            error?.response?.data?.message ||
              'Could not load the email preview.',
          )
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    }, PREVIEW_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [show, payloadKey])

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title className='font-18 font-semibold'>
          Scheduling Email Preview
        </Modal.Title>
      </Modal.Header>
      {/* One scrollbar: the email iframe scrolls, the modal body does not */}
      <Modal.Body
        className='d-flex flex-column'
        style={{ height: '75vh', overflow: 'hidden' }}>
        <p className='text-muted font-14 mb-2'>
          This is the email the client receives when the SOW goes live. Nothing
          is sent from here.
        </p>
        {isPaymentRequired && (
          <p className='font-14 mb-2'>
            Payment is required, so this email is sent after payment, not when
            the SOW is created.
          </p>
        )}
        {isCoachMissingVideoCallLink && (
          <p className='text-red-800 font-14 mb-2'>
            This coach has no video call link, so the Schedule Your Abroad
            Coaching Session email will not be sent. The preview below still
            shows it.
          </p>
        )}

        {isLoading && (
          <div className='d-flex flex-grow-1 justify-content-center align-items-center'>
            <ABSpinner />
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className='text-red-800 py-4'>{errorMessage}</div>
        )}

        {!isLoading && preview && (
          <>
            <dl className='row font-14 mb-2'>
              <dt className='col-sm-2 text-muted'>Subject</dt>
              <dd className='col-sm-10 mb-1'>{preview.subject}</dd>
              <dt className='col-sm-2 text-muted'>From</dt>
              <dd className='col-sm-10 mb-1'>{preview.from}</dd>
              <dt className='col-sm-2 text-muted'>To</dt>
              <dd className='col-sm-10 mb-1'>{preview.to?.join(', ')}</dd>
              {preview.cc?.length > 0 && (
                <>
                  <dt className='col-sm-2 text-muted'>CC</dt>
                  <dd className='col-sm-10 mb-1'>{preview.cc.join(', ')}</dd>
                </>
              )}
            </dl>
            <iframe
              title='Scheduling email preview'
              sandbox=''
              srcDoc={withPreviewOnlyStyle(preview.html)}
              className='w-100 flex-grow-1 border rounded'
              style={{ minHeight: 0 }}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type='button'
          variant='secondary-light'
          className='text-uppercase line-height-normal'
          onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SOWEmailPreviewModal
